import React from "react"

import {
  FEATURE_FLAGS,
  useOrganizationFeatureFlagScope,
  usePostHydrationFeatureFlagValue,
} from "@/feature-flags"
import {
  Alert,
  AlertDescription,
  AlertTitle,
  BetaBadge,
  Button,
  Card,
  CardContent,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingRow,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@coval/ui"
import { AlertTriangle, Pencil, RefreshCcw, VolumeX } from "lucide-react"

import {
  ResourceNameCard,
  type ResourceNameCardProps,
} from "@/components/resource-header/ResourceNameCard"
import { ReadOnlyCopyButton } from "@/components/version-history/ReadOnlyCopyButton"
import {
  ReadOnlyFieldset,
  useIsInReadOnlyRegion,
} from "@/components/version-history/ReadOnlyRegion"
import type { VoicePersonaConfig } from "@/types/ModelConfigs/VoiceModelConfigs"
import { getAvatarColor } from "@/utils/avatarIdentity"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import {
  EYES_VARIANTS,
  HAIR_VARIANTS,
  LIPS_VARIANTS,
} from "../utils/personaModalUtils"
import {
  AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE,
  type PersonaFormErrors,
} from "../utils/personaValidation"
import { PersonaAdvancedFields } from "./PersonaAdvancedFields"
import { PersonaCallEnvironmentCard } from "./PersonaCallEnvironmentCard"
import { PersonaVoiceCard } from "./PersonaVoiceCard"

interface PersonaFormFieldsProps {
  controller: PersonaFormController
  embedded?: boolean
  validationErrors?: PersonaFormErrors
  /** Shows the persona name card, with the avatar control at its right end, in place of the avatar card. */
  nameField?: Omit<
    ResourceNameCardProps,
    "trailing" | "children" | "invalid" | "errorId"
  >
}

function joinErrors(...errors: Array<string | undefined>): string | undefined {
  const uniqueErrors = Array.from(
    new Set(errors.filter((error): error is string => Boolean(error))),
  )
  return uniqueErrors.length > 0 ? uniqueErrors.join(" ") : undefined
}

const INTERRUPTION_RATE_OPTIONS: Array<{
  value: NonNullable<VoicePersonaConfig["interruption_rate"]>
  label: string
}> = [
  { value: "NONE", label: "None" },
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
]

const FACE_VARIANT_TABS = [
  { value: "hair", label: "Hair", variants: HAIR_VARIANTS },
  { value: "eyes", label: "Eyes", variants: EYES_VARIANTS },
  { value: "lips", label: "Lips", variants: LIPS_VARIANTS },
] as const

/**
 * Presentational persona form body shared by the editable form (`PersonaForm`)
 * and read-only views. All state lives in the controller; a caller makes it
 * read-only by wrapping it in `ReadOnlyRegionProvider`.
 */
export const PersonaFormFields: React.FC<PersonaFormFieldsProps> = ({
  controller,
  embedded = false,
  nameField,
  validationErrors,
}) => {
  const promptedInterruptionsFlag = usePostHydrationFeatureFlagValue(
    FEATURE_FLAGS.PROMPTED_INTERRUPTIONS,
  )
  const { isReady: featureFlagScopeReady } = useOrganizationFeatureFlagScope()
  const showInterruptionInstructions =
    featureFlagScopeReady && promptedInterruptionsFlag === true
  const {
    selectedPersona,
    isBuiltIn,
    isSpeechDisabled,
    isLegacy,
    formData,
    handleChange,
    isPromptExpanded,
    setIsPromptExpanded,
    isInSilentMode,
    randomColorIndex,
    isFaceSelectorOpen,
    setIsFaceSelectorOpen,
    hairPreviews,
    eyesPreviews,
    lipsPreviews,
    handleFaceCustomization,
    handleRegenerateSeed,
    updateMetadata,
  } = controller

  const isInReadOnlyRegion = useIsInReadOnlyRegion()
  const avatarErrorId = React.useId()
  const hasPrompt = (formData.simulated_user_prompt ?? "").trim().length > 0
  // A disabled textarea blocks text selection, so a prompt that can't be
  // edited offers a copy control instead.
  const showPromptCopy = hasPrompt && (isInReadOnlyRegion || isSpeechDisabled)
  const metadata = formData.metadata as VoicePersonaConfig
  const promptError = validationErrors?.simulated_user_prompt
  const avatarError = joinErrors(
    validationErrors?.avatar_url,
    validationErrors?.face_customization,
  )
  const voiceError =
    validationErrors?.metadata ===
    AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE
      ? undefined
      : validationErrors?.metadata
  const advancedError = joinErrors(
    promptError,
    validationErrors?.persona_role,
    validationErrors?.persona_personality,
    validationErrors?.persona_speech,
  )

  const avatarImage = (frameClassName: string, imageClassName: string) => (
    <div
      className={cn(
        "flex items-end justify-center overflow-hidden rounded-full",
        frameClassName,
      )}
      style={{
        backgroundColor: getAvatarColor(
          selectedPersona?.id || "",
          randomColorIndex,
        ),
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
      <img
        src={formData.avatar_url}
        className={cn("rounded-full", imageClassName)}
        alt="Persona avatar"
      />
    </div>
  )

  const avatarEditButton = (positionClassName: string) => (
    <Button
      type="button"
      icon
      variant="secondary"
      tooltip="Edit persona avatar"
      className={cn("absolute z-10 rounded-full", positionClassName)}
      onClick={() => setIsFaceSelectorOpen(true)}
      disabled={isBuiltIn}
      aria-describedby={avatarError ? avatarErrorId : undefined}
    >
      <Pencil className="h-3.5 w-3.5" />
    </Button>
  )

  const avatarErrorMessage = avatarError ? (
    <p
      id={avatarErrorId}
      role="alert"
      className="mt-3 text-xs text-coval-danger-text"
    >
      {avatarError}
    </p>
  ) : null

  const facePreviews = {
    hair: hairPreviews,
    eyes: eyesPreviews,
    lips: lipsPreviews,
  }

  return (
    <div
      className={cn(
        "space-y-3",
        embedded &&
          "divide-y divide-coval-border space-y-0 [&>div]:rounded-none [&>div]:border-0",
      )}
    >
      {isInSilentMode && (
        <Alert
          variant="info"
          icon={<VolumeX />}
          className="border border-coval-border bg-surface-0 text-coval-text"
        >
          <AlertTitle>Silent Mode is on</AlertTitle>
          <AlertDescription>
            This persona never speaks, so its prompt, voice, and call
            environment are not used. Turn Silent Mode off under Advanced to
            edit them.
          </AlertDescription>
        </Alert>
      )}

      {nameField ? (
        <ResourceNameCard
          {...nameField}
          invalid={Boolean(avatarError)}
          errorId={avatarErrorId}
          trailing={
            <ReadOnlyFieldset>
              <div className="relative">
                {avatarImage("h-14 w-14", "h-16 w-16 pt-2")}
                {avatarEditButton("-bottom-1.5 -right-1.5")}
              </div>
            </ReadOnlyFieldset>
          }
        >
          {avatarErrorMessage}
        </ResourceNameCard>
      ) : (
        <Card
          aria-invalid={avatarError ? true : undefined}
          aria-describedby={avatarError ? avatarErrorId : undefined}
          className={avatarError ? "border-coval-danger-border" : undefined}
        >
          <ReadOnlyFieldset>
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                <div className="relative shrink-0">
                  {avatarImage("h-20 w-20", "h-24 w-24 pt-3")}
                  {avatarEditButton("-bottom-1 -right-1")}
                </div>
              </div>
              {avatarErrorMessage}
            </CardContent>
          </ReadOnlyFieldset>
        </Card>
      )}

      {isLegacy && (
        <Alert variant="warning" icon={<AlertTriangle />}>
          <strong>Voice update required:</strong> select a voice and language to
          update this persona.
        </Alert>
      )}

      <PersonaVoiceCard controller={controller} error={voiceError} />

      <PersonaCallEnvironmentCard controller={controller} />

      <Card className="w-full">
        <ReadOnlyFieldset>
          <CardContent className="space-y-3 p-6">
            <SettingRow
              label="Interruption Rate"
              tooltip="How often the persona will interrupt your agent."
            >
              <Select
                value={metadata.interruption_rate || "NONE"}
                onValueChange={(selected) =>
                  updateMetadata({
                    interruption_rate:
                      selected as VoicePersonaConfig["interruption_rate"],
                  })
                }
                disabled={isSpeechDisabled}
              >
                <SelectTrigger aria-label="Interruption Rate" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERRUPTION_RATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingRow>
            {showInterruptionInstructions && (
              <SettingRow
                label={
                  <span className="inline-flex items-center gap-2">
                    Interruption instructions
                    {/* The badge sits inside the switch's label; keep a click on it from toggling the switch. */}
                    <BetaBadge onClick={(event) => event.preventDefault()} />
                  </span>
                }
                tooltip="When on, the persona interrupts the agent when its prompt or the test case tells it to."
              >
                {(controlId) => (
                  <Switch
                    id={controlId}
                    checked={
                      metadata.interruption_instructions_enabled === true
                    }
                    onCheckedChange={(checked) =>
                      updateMetadata({
                        interruption_instructions_enabled: checked,
                      })
                    }
                    disabled={isSpeechDisabled}
                  />
                )}
              </SettingRow>
            )}
          </CardContent>
        </ReadOnlyFieldset>
      </Card>

      <PersonaAdvancedFields controller={controller} error={advancedError}>
        <ReadOnlyFieldset className="pb-4">
          <FormField
            label="Persona prompt (optional)"
            description="Add personality or behavior instructions. Leave blank to follow the test case alone."
            tooltip="Personality, tone, and speech patterns the persona acts out. Does not control voice or pronunciation."
            error={promptError}
          >
            {(ids) => (
              <div className="relative">
                <Textarea
                  {...ids}
                  name="simulated_user_prompt"
                  rows={4}
                  className={cn("resize-none", showPromptCopy && "pr-11")}
                  value={formData.simulated_user_prompt}
                  onChange={handleChange}
                  error={Boolean(promptError)}
                  disabled={isSpeechDisabled}
                  data-testid="persona-prompt-textarea"
                />
                {showPromptCopy ? (
                  <ReadOnlyCopyButton
                    value={formData.simulated_user_prompt ?? ""}
                    label="Copy prompt"
                    className="absolute right-2 top-2"
                  />
                ) : null}
              </div>
            )}
          </FormField>
        </ReadOnlyFieldset>
      </PersonaAdvancedFields>

      <Dialog open={isFaceSelectorOpen} onOpenChange={setIsFaceSelectorOpen}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customize Face</DialogTitle>
          </DialogHeader>
          {/* Body never scrolls; only the variant grid does, so the preview,
              tabs, and footer stay put while browsing. */}
          <DialogBody className="overflow-hidden">
            <div className="grid h-[240px] grid-cols-2 gap-6">
              <div className="relative flex h-full flex-col items-center justify-center">
                <div className="relative h-32 w-32">
                  <div
                    className="flex h-32 w-32 items-end justify-center overflow-hidden rounded-full"
                    style={{
                      backgroundColor: getAvatarColor(
                        selectedPersona?.id || "",
                        randomColorIndex,
                      ),
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
                    <img
                      src={formData.avatar_url}
                      className="h-40 w-40 rounded-full pt-5"
                      alt="Preview"
                    />
                  </div>
                  <Button
                    type="button"
                    icon
                    variant="secondary"
                    tooltip="Regenerate avatar"
                    className="absolute -bottom-1 -right-1 z-10 rounded-full"
                    onClick={() => {
                      handleRegenerateSeed()
                    }}
                  >
                    <RefreshCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Tabs
                defaultValue="hair"
                className="flex h-full min-h-0 flex-col"
              >
                <TabsList className="shrink-0">
                  {FACE_VARIANT_TABS.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {FACE_VARIANT_TABS.map((tab) => (
                  <TabsContent
                    key={tab.value}
                    value={tab.value}
                    className="min-h-0 flex-1 overflow-y-auto pr-1"
                  >
                    <div className="grid grid-cols-6 gap-2">
                      {tab.variants.map((variant, idx) => (
                        <button
                          key={variant}
                          className={`rounded-full border-2 ${formData.face_customization?.[tab.value] === variant ? "border-coval-border-active" : "border-transparent"}`}
                          onClick={() => {
                            handleFaceCustomization(tab.value, variant)
                          }}
                          type="button"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
                          <img
                            src={`${facePreviews[tab.value][idx]}&scale=150&translateY=25`}
                            className="h-9 w-9 rounded-full object-cover"
                            alt={variant}
                          />
                        </button>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsFaceSelectorOpen(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPromptExpanded} onOpenChange={setIsPromptExpanded}>
        <DialogContent className="w-full max-w-4xl max-h-[95vh] min-h-[800px] h-full flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Edit Prompt</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex-1 min-h-0 p-6">
            <Textarea
              aria-label="Prompt"
              name="simulated_user_prompt"
              value={formData.simulated_user_prompt}
              onChange={handleChange}
              disabled={isBuiltIn}
              className="h-full min-h-full resize-none"
            />
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button type="button" onClick={() => setIsPromptExpanded(false)}>
              Done (esc)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
